import { Menu } from '@headlessui/react';

import { ArrowRightIcon } from '~components/icons/ArrowRightIcon';
import { ArrowUpRightIcon } from '~components/icons/ArrowUpRightIcon';
import { ClipboardAddIcon } from '~components/icons/ClipboardAddIcon';
import { CollectionIcon } from '~components/icons/CollectionIcon';
import { ConnectionIcon } from '~components/icons/ConnectionIcon';
import { CropIcon } from '~components/icons/CropIcon';
import { DownloadIcon } from '~components/icons/DownloadIcon';
import { FolderSymlinkIcon } from '~components/icons/FolderSymlinkIcon';
import { LinkIcon } from '~components/icons/LinkIcon';
import { PenIcon } from '~components/icons/PenIcon';
import { PlayIcon } from '~components/icons/PlayIcon';
import useLabels from '~hooks/useLabels';
import { AssetId } from '~modules/media-library/model/Asset';
import { AssetActions } from '~modules/media-library/model/AssetActions';
import { TeaserMenuItemButton } from '~modules/media-library/view/Teaser/TeaserMenuItemButton';

type TeaserMenuPanelProps = {
  assetId: AssetId;
  AssetActions: AssetActions;
};

export function TeaserMenuPanel({ assetId, AssetActions }: TeaserMenuPanelProps) {
  const {
    btnMediaLibraryActionDetails,
    btnMediaLibraryActionDownload,
    btnMediaLibraryActionAddToMyfiles,
    btnMediaLibraryActionUseWeb2Print,
    btnMediaLibraryActionUseCanva,
    btnMediaLibraryActionCopyId,
    btnMediaLibraryActionCopyLink,
    btnMediaLibraryActionCropImage,
    btnMediaLibraryActionOpenCollection,
    btnMediaLibraryActionPlay,
    btnMediaLibraryActionUseRaiseRequestAssets,
    btnMediaLibraryActionJsmContactForm,
  } = useLabels([
    'btnMediaLibraryActionDetails',
    'btnMediaLibraryActionDownload',
    'btnMediaLibraryActionAddToMyfiles',
    'btnMediaLibraryActionUseWeb2Print',
    'btnMediaLibraryActionUseCanva',
    'btnMediaLibraryActionCopyId',
    'btnMediaLibraryActionCopyLink',
    'btnMediaLibraryActionCropImage',
    'btnMediaLibraryActionOpenCollection',
    'btnMediaLibraryActionPlay',
    'btnMediaLibraryActionUseRaiseRequestAssets',
    'btnMediaLibraryActionJsmContactForm',
  ]);
  const {
    onDetails,
    openContributionWizard,
    onDownload,
    onAddToMyfiles,
    onUseWeb2Print,
    onUseCanva,
    onCopyLink,
    onCrop,
    onCollection,
    onPlay,
    onConfirmDelete,
    onUseRaiseRequestAssets,
    onApprovalForm,
    onRestartContribution,
    onJsmContactForm,
  } = AssetActions;

  // disable copy id - NEOBS-898
  const onCopyId = undefined;

  return (
    <div className="ml--teaser-menu--panel rounded bg-white shadow-xl flex flex-col w-full">
      <div className="flex flex-col gap-1 p-3">
        {!!onDetails ? (
          <Menu.Item>
            {({ active }) => (
              <TeaserMenuItemButton
                title=""
                className="ml-icon-arrow-right"
                onClick={() => onDetails(assetId)}
                focus={active}
              >
                <ArrowRightIcon className="w-5 h-5 mr-2" />
                {btnMediaLibraryActionDetails}
              </TeaserMenuItemButton>
            )}
          </Menu.Item>
        ) : null}
        {!!onCollection ? (
          <Menu.Item>
            {({ active }) => (
              <TeaserMenuItemButton
                title=""
                className="ml-icon-collection"
                onClick={() => onCollection(assetId)}
                focus={active}
              >
                <CollectionIcon className="w-5 h-5 mr-2" />
                {btnMediaLibraryActionOpenCollection}
              </TeaserMenuItemButton>
            )}
          </Menu.Item>
        ) : null}
        {!!onPlay ? (
          <Menu.Item>
            {({ active }) => (
              <TeaserMenuItemButton title="" className="ml-icon-play" onClick={() => onPlay(assetId)} focus={active}>
                <PlayIcon className="w-5 h-5 mr-2" />
                {btnMediaLibraryActionPlay}
              </TeaserMenuItemButton>
            )}
          </Menu.Item>
        ) : null}
        {!!onDownload ? (
          <Menu.Item>
            {({ active }) => (
              <TeaserMenuItemButton
                title=""
                className="ml-icon-download"
                onClick={() => onDownload(assetId)}
                focus={active}
              >
                <DownloadIcon className="w-5 h-5 mr-2" />
                {btnMediaLibraryActionDownload}
              </TeaserMenuItemButton>
            )}
          </Menu.Item>
        ) : null}
        {!!onAddToMyfiles ? (
          <Menu.Item>
            {({ active }) => (
              <TeaserMenuItemButton
                title=""
                className="ml-icon-symlink"
                onClick={() => onAddToMyfiles(assetId)}
                focus={active}
              >
                <FolderSymlinkIcon className="w-5 h-5 mr-2" />
                {btnMediaLibraryActionAddToMyfiles}
              </TeaserMenuItemButton>
            )}
          </Menu.Item>
        ) : null}
        {!!onUseRaiseRequestAssets ? (
          <Menu.Item>
            {({ active }) => (
              <TeaserMenuItemButton
                title=""
                className="ml-icon-arrow-diagonally"
                onClick={() => onUseRaiseRequestAssets(assetId)}
                focus={active}
              >
                <ConnectionIcon className="w-5 h-5 mr-2" />
                {btnMediaLibraryActionUseRaiseRequestAssets}
              </TeaserMenuItemButton>
            )}
          </Menu.Item>
        ) : null}
        {!!onUseWeb2Print ? (
          <Menu.Item>
            {({ active }) => (
              <TeaserMenuItemButton
                title=""
                className="ml-icon-arrow-diagonally"
                onClick={() => onUseWeb2Print(assetId)}
                focus={active}
              >
                <ArrowUpRightIcon className="w-5 h-5 mr-2" />
                {btnMediaLibraryActionUseWeb2Print}
              </TeaserMenuItemButton>
            )}
          </Menu.Item>
        ) : null}
        {!!onUseCanva ? (
          <Menu.Item>
            {({ active }) => (
              <TeaserMenuItemButton
                title=""
                className="ml-icon-arrow-diagonally"
                onClick={() => onUseCanva(assetId)}
                focus={active}
              >
                <ArrowUpRightIcon className="w-5 h-5 mr-2" />
                {btnMediaLibraryActionUseCanva}
              </TeaserMenuItemButton>
            )}
          </Menu.Item>
        ) : null}

        {!!onJsmContactForm ? (
          <Menu.Item>
            {({ active }) => (
              <TeaserMenuItemButton
                title=""
                className="ml-icon-arrow-diagonally"
                onClick={() => onJsmContactForm(assetId)}
                focus={active}
              >
                <PenIcon className="w-5 h-5 mr-2" />
                <span
                  className="leading-4"
                  dangerouslySetInnerHTML={{ __html: btnMediaLibraryActionJsmContactForm }}
                ></span>
              </TeaserMenuItemButton>
            )}
          </Menu.Item>
        ) : null}
        {!!onCopyId ? (
          <Menu.Item>
            {({ active }) => (
              <TeaserMenuItemButton
                title=""
                className="ml-icon-clipboard"
                onClick={() => onCopyId(assetId)}
                focus={active}
              >
                <ClipboardAddIcon className="w-5 h-5 mr-2" />
                {btnMediaLibraryActionCopyId}
              </TeaserMenuItemButton>
            )}
          </Menu.Item>
        ) : null}
        {!!onCopyLink ? (
          <Menu.Item>
            {({ active }) => (
              <TeaserMenuItemButton
                title=""
                className="ml-icon-link"
                onClick={() => onCopyLink(assetId)}
                focus={active}
              >
                <LinkIcon className="w-5 h-5 mr-2" />
                {btnMediaLibraryActionCopyLink}
              </TeaserMenuItemButton>
            )}
          </Menu.Item>
        ) : null}
        {!!onCrop ? (
          <Menu.Item>
            {({ active }) => (
              <TeaserMenuItemButton title="" className="ml-icon-crop" onClick={() => onCrop(assetId)} focus={active}>
                <CropIcon className="w-5 h-5 mr-2" />
                {btnMediaLibraryActionCropImage}
              </TeaserMenuItemButton>
            )}
          </Menu.Item>
        ) : null}
        {!!openContributionWizard ? (
          <Menu.Item>
            {({ active }) => (
              <TeaserMenuItemButton
                title=""
                className="ml-icon-arrow-right"
                onClick={() => {
                  openContributionWizard(assetId, 'edit-single');
                }}
                focus={active}
              >
                <ArrowRightIcon className="w-5 h-5 mr-2" />
                Edit
              </TeaserMenuItemButton>
            )}
          </Menu.Item>
        ) : null}
        {!!openContributionWizard ? (
          <Menu.Item>
            {({ active }) => (
              <TeaserMenuItemButton
                title=""
                className="ml-icon-arrow-right"
                onClick={() => {
                  openContributionWizard(assetId, 'replace');
                }}
                focus={active}
              >
                <ArrowRightIcon className="w-5 h-5 mr-2" />
                Replace File
              </TeaserMenuItemButton>
            )}
          </Menu.Item>
        ) : null}
        {!!onApprovalForm ? (
          <Menu.Item>
            {({ active }) => (
              <TeaserMenuItemButton
                title=""
                className="ml-icon-crop"
                onClick={() => onApprovalForm(assetId)}
                focus={active}
              >
                <ArrowRightIcon className="w-5 h-5 mr-2" />
                Approval
              </TeaserMenuItemButton>
            )}
          </Menu.Item>
        ) : null}
        {!!onRestartContribution ? (
          <Menu.Item>
            {({ active }) => (
              <TeaserMenuItemButton
                title=""
                className="ml-icon-crop"
                onClick={() => onRestartContribution(assetId)}
                focus={active}
              >
                <ArrowRightIcon className="w-5 h-5 mr-2" />
                Restart Contribution
              </TeaserMenuItemButton>
            )}
          </Menu.Item>
        ) : null}
        {!!onConfirmDelete ? (
          <Menu.Item>
            {({ active }) => (
              <TeaserMenuItemButton
                title=""
                className="ml-icon-crop"
                onClick={() => onConfirmDelete(assetId)}
                focus={active}
              >
                <ArrowRightIcon className="w-5 h-5 mr-2" />
                Delete
              </TeaserMenuItemButton>
            )}
          </Menu.Item>
        ) : null}
      </div>
    </div>
  );
}
